// Page: CreateCampaign
// Purpose: Full create-campaign flow. For non-premium users, shows premium
// gating UI without running hooks. For premium users, wires up form, drafts,
// AI generation, template selection, submission, and live preview.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../../firebase/firebase';
import { Card, CardContent, CardHeader } from '../../../../components/ui/layout/card'; 
import { PhonePreview } from '../../../../components/create-campaign/preview/PhonePreview';
import { LoadingModal } from '../../../../components/create-campaign/modals/loading-modal.jsx';
import { AIModal } from '../../../../components/create-campaign/modals/AIModal';
import { HelpModal } from '../../../../components/create-campaign/modals/HelpModal';
import { CampaignTemplateModal } from '../../../../components/create-campaign/modals/CampaignTemplateModal';
import { DraftSection } from '../../../../components/create-campaign/components/DraftSection';
import { CampaignFormContainer } from '../../../../components/create-campaign/components/CampaignFormContainer';
import CampaignLimitReached from '../../../../components/create-campaign/components/CampaignLimitReached';
import { themes } from '../../../../components/create-campaign/utils/campaignThemes';
import { useCampaignForm } from '../../../../components/create-campaign/hooks/useCampaignForm';
import { useCampaignDrafts } from '../../../../components/create-campaign/hooks/useCampaignDrafts';
import { useAIGeneration } from '../../../../components/create-campaign/hooks/useAIGeneration';
import { useCampaignSubmit } from '../../../../components/create-campaign/hooks/useCampaignSubmit';
import { useTemplateSelection } from '../../../../components/create-campaign/hooks/useTemplateSelection';
import { useNamespace } from '../../../../context/namespace';
import { useAuth } from '../../../../context/auth';
import { CreateCampaignSkeleton } from '../../../../components/ui/feedback/skeleton.jsx';
import { Eye, Shield, Users, Crown, ChevronRight } from 'lucide-react';
import { VideoModal } from '../../../../components/ui/modals/VideoModal';
import { IntroductionPageButtons } from '../../../../components/create-campaign/steps/IntroductionPage';
import SEOHead from '../../../../components/shared/SEOHead';
import Footer from '../../../../components/shared/Footer';

// Add custom styles to ensure smooth transitions
const styles = {
  container: {
    transition: 'opacity 0.15s ease-in-out',
  }
};

// Add a new component for readonly permission error
const ReadOnlyPermissionMessage = ({ userPermission, currentNamespace }) => {
  return (
    <Card className="w-full border border-gray-200 dark:border-gray-800 shadow-sm">
      <CardHeader className="pb-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-900/20">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <Eye className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">
                Read-Only Access
              </h3>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                You currently have read-only access to the <span className="font-medium">{currentNamespace}</span> namespace.
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Your Options</h3>
          
          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 transition-colors bg-white dark:bg-gray-900">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0 rounded-full bg-amber-100 p-1.5 dark:bg-amber-900/30">
                <Shield className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Contact Administrator</h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Contact your namespace administrator to request Editor or Admin permissions for this namespace.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-800/60 dark:bg-primary-900/20">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0 rounded-full bg-primary-100 p-1.5 dark:bg-primary-900/50">
                <Users className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Switch Namespace</h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Switch to another namespace where you have Editor or Admin permissions to create campaigns.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Permission Levels</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Read-Only:</span>
              <span className="font-medium text-gray-900 dark:text-white">View campaigns only</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Editor:</span>
              <span className="font-medium text-gray-900 dark:text-white">Create and edit campaigns</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Admin:</span>
              <span className="font-medium text-gray-900 dark:text-white">Full access including user management</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function CreateCampaign() {
  const navigate = useNavigate();
  const { user, fetchUserProfile } = useAuth();
  
  console.log('=== CreateCampaign RENDER ===');
  console.log('CreateCampaign rendering with user:', user?.planLevel);
  
  // Check if user has premium subscription - this is the ONLY check we need before deciding what to render
  const isPremium = user?.planLevel === 'premium' || user?.subscriptionStatus?.currentPlanLevel === 'premium';
  
  // FOR NON-PREMIUM USERS: Return the premium banner view immediately
  // This happens before any hooks are called or any API requests are made
  if (!isPremium) {
    // Initialize minimal form state for UI consistency without any API calls
    // Using inline implementation to avoid API calls from the actual hook
    const form = {
      currentStep: 0,
      formData: {
        name: "",
        title: "",
        description: ""
      },
      showPreview: true,
      handleInputChange: () => {},
      updateFormData: () => {},
      aiGeneratedFields: {},
      setAiGeneratedFields: () => {},
      surveyQuestions: [],
      setSurveyQuestions: () => {},
      handleNext: () => {},
      handlePrevious: () => {},
      clearFormCache: () => {},
      selectedTheme: "modern",
      setSelectedTheme: () => {},
      gradientColors: ["#4F46E5", "#7C3AED"],
      setGradientColors: () => {},
      gradientDirection: "to-r",
      setGradientDirection: () => {},
      hexText: "#FFFFFF",
      setHexText: () => {},
      previewImage: null,
      handleRemoveImage: () => {},
      hasExplainerVideo: false,
      setHasExplainerVideo: () => {},
      explainerVideo: null,
      setExplainerVideo: () => {}
    };

    // Empty handleImageChange function
    const handleImageChange = () => {};
    
    return (
      <div className="p-6 w-full max-w-[1600px] mx-auto" style={styles.container}>
        {/* Premium feature notification */}
        <div className="mb-6 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800/50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Crown className="h-5 w-5 text-purple-500 dark:text-purple-400 mr-3" />
              <p className="text-sm text-purple-800 dark:text-purple-300">
                Campaign creation is a premium feature. Upgrade to create and manage multiple campaigns.
              </p>
            </div>
            <button 
              onClick={() => navigate('/app/account', { state: { fromFeature: true, returnPath: '/app/campaigns/new', featureName: 'Campaign Creation' }})}
              className="ml-4 flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-md text-xs font-medium"
            >
              Upgrade
              <ChevronRight className="ml-1 h-3 w-3" />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create New Campaign</h1>
              <div className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-xs font-medium shadow-sm">
                <Crown className="w-3 h-3 mr-0.5" />
                Premium
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Set up your campaign details and customize your survey questions.
            </p>
            <div className="relative max-w-[800px] h-1 bg-gray-200 dark:bg-gray-800 mt-4">
              <div
                className="absolute inset-y-0 left-0 bg-primary-600 dark:bg-primary-400 transition-all duration-500"
                style={{ width: `${((form.currentStep + 1) / 6) * 100}%` }}
              />
            </div>
          </div>

          <div className={`grid grid-cols-1 ${form.showPreview ? 'lg:grid-cols-[800px,auto]' : ''} gap-6 items-start ${!form.showPreview ? 'w-full' : ''}`}>
            <CampaignFormContainer
              currentStep={form.currentStep}
              formData={form.formData}
              handleInputChange={form.handleInputChange}
              updateFormData={form.updateFormData}
              aiGeneratedFields={form.aiGeneratedFields}
              setAiGeneratedFields={form.setAiGeneratedFields}
              surveyQuestions={form.surveyQuestions}
              setSurveyQuestions={form.setSurveyQuestions}
              handleNext={form.handleNext}
              handlePrevious={form.handlePrevious}
              handleSubmit={() => {}}
              isSubmitting={false}
              isSavingDraft={false}
              handleSaveDraft={() => {}}
              isEditingDraft={false}
              setIsAIModalOpen={() => {}}
              handleUseTemplate={() => {}}
              clearSelectedTemplate={() => {}}
              selectedTemplate={null}
              selectedTheme={form.selectedTheme}
              setSelectedTheme={form.setSelectedTheme}
              gradientColors={form.gradientColors}
              setGradientColors={form.setGradientColors}
              gradientDirection={form.gradientDirection}
              setGradientDirection={form.setGradientDirection}
              hexText={form.hexText}
              setHexText={form.setHexText}
              themes={themes}
              previewImage={form.previewImage}
              handleImageChange={handleImageChange}
              handleRemoveImage={form.handleRemoveImage}
              setIsHelpOpen={() => {}}
              hasExplainerVideo={form.hasExplainerVideo}
              setHasExplainerVideo={form.setHasExplainerVideo}
              explainerVideo={form.explainerVideo}
              setExplainerVideo={form.setExplainerVideo}
              error={null}
              currentNamespace={""}
              userPermission={null}
              renderCustomButtons={form.currentStep === 0}
              renderCustomButtonsContent={
                form.currentStep === 0 ? (
                  <IntroductionPageButtons 
                    setIsVideoModalOpen={() => {}} 
                    onNext={form.handleNext}
                  />
                ) : null
              }
            />

            <PhonePreview
              selectedTheme={form.selectedTheme}
              themes={themes}
              previewImage={form.previewImage}
              formData={form.formData}
              surveyQuestions={form.surveyQuestions}
              currentStep={form.currentStep}
              gradientColors={form.gradientColors}
              gradientDirection={form.gradientDirection}
              hexText={form.hexText}
              hasExplainerVideo={form.hasExplainerVideo}
              explainerVideo={form.explainerVideo}
            />
          </div>
        </div>

        <Footer />
      </div>
    );
  }
  
  // FROM HERE ONWARDS - ONLY FOR PREMIUM USERS
  // All the hooks and API calls will only happen for premium users
  const { fetchUserProfile: fetchProfile, loading: authLoading } = useAuth();
  const [isDraftsOpen, setIsDraftsOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  
  // Initialize all loading states
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isInitialRenderComplete, setIsInitialRenderComplete] = useState(false);
  const [isLoadingCampaignCount, setIsLoadingCampaignCount] = useState(true);
  
  // Add a ref to track if profile has been refreshed to prevent multiple requests
  const hasRefreshedProfileRef = useRef(false);
  
  // Get the current namespace from context
  const { 
    currentNamespace, 
    namespaces: contextNamespaces, // Use context namespaces
    isLoading: namespacesLoading,  // Use context loading state
  } = useNamespace();
  
  // Find the current namespace ID and user permission
  const currentNamespaceObj = contextNamespaces.find(ns => ns.name === currentNamespace);
  const currentNamespaceId = currentNamespaceObj?.id;
  
  // Get the user's permission for the current namespace
  const userPermission = currentNamespaceObj?.currentUserPermission || null;
  
  // Check if user has reached campaign limit
  const [hasReachedCampaignLimit, setHasReachedCampaignLimit] = useState(false);
  const [campaignsCount, setCampaignsCount] = useState(0);
  const [campaignLimit, setCampaignLimit] = useState(1); // Default to Standard plan limit
  
  // Immediately set loading states to false for non-premium users - only needed in case isPremium changes
  useEffect(() => {
    // Only proceed with profile refresh for premium users
    const refreshUserProfile = async () => {
      // Check if we've already refreshed the profile to prevent multiple calls
      if (hasRefreshedProfileRef.current) {
        console.log('Profile already refreshed, skipping additional refresh');
        return;
      }
      
      if (auth.currentUser) {
        console.log('Refreshing profile data on CreateCampaign mount');
        try {
          await fetchProfile(auth.currentUser);
          // Mark that we've refreshed the profile
          hasRefreshedProfileRef.current = true;
        } catch (error) {
          console.error('Error refreshing profile:', error);
        }
      }
    };
    
    refreshUserProfile();
  }, [fetchProfile]);
  
  // Reset the refresh flag when the component unmounts
  useEffect(() => {
    return () => {
      hasRefreshedProfileRef.current = false;
    };
  }, []);
  
  // Determine campaign limit based on user's plan and check if limit is reached
  useEffect(() => {
    if (user) {
      const userPlan = user.planLevel || 'basic';
      let limit = 1; // Default for Standard/basic plan
      
      if (userPlan === 'premium') {
        limit = 25;
      }
      
      setCampaignLimit(limit);
      
      // Get campaign count directly from user profile
      const campaignsCount = user.campaigns || 0;
      setCampaignsCount(campaignsCount);
      
      // Check if user has reached their limit
      setHasReachedCampaignLimit(campaignsCount >= limit);
      setIsLoadingCampaignCount(false);
    }
  }, [user]);
  
  // Use the form hook
  const form = useCampaignForm();
  
  // Use the drafts hook - only needed for premium users but initialize for UI consistency
  const drafts = useCampaignDrafts(
    form.formData,
    form.updateFormData,
    form.clearFormCache,
    form.surveyQuestions,
    form.setSurveyQuestions,
    form.setAiGeneratedFields,
    form.selectedTheme,
    form.setSelectedTheme,
    form.gradientColors,
    form.setGradientColors,
    form.gradientDirection,
    form.setGradientDirection,
    form.hexText,
    form.setHexText,
    form.previewImage,
    form.setPreviewImage,
    form.hasExplainerVideo,
    form.setHasExplainerVideo,
    form.explainerVideo,
    form.setExplainerVideo,
    form.setCurrentStep,
    setIsDraftsOpen,
    currentNamespaceId, // Pass namespaceId directly
    isPremium           // Pass the live premium status
  );
  
  // Use the AI generation hook
  const ai = useAIGeneration(
    form.formData,
    form.updateFormData,
    form.setSurveyQuestions,
    form.setAiGeneratedFields,
    setIsAIModalOpen,
    form.setCurrentStep,
    isPremium ? currentNamespaceId : null  // Keep conditional logic here for AI hook
  );
  
  // Use the campaign submission hook with namespace ID
  const submit = useCampaignSubmit(
    form.formData,
    form.surveyQuestions,
    form.selectedTheme,
    form.gradientColors,
    form.gradientDirection,
    form.hexText,
    form.previewImage,
    form.hasExplainerVideo,
    form.explainerVideo,
    form.clearFormCache,
    form.resetForm,
    isPremium ? currentNamespaceId : null,  // Keep conditional logic here for submit hook
    // Draft cleanup parameters
    drafts.selectedDraftId,
    drafts.handleDeleteDraft
  );
  
  // Use the template selection hook
  const template = useTemplateSelection(
    form.formData,
    form.updateFormData,
    form.setSelectedTheme,
    form.setGradientColors,
    form.setGradientDirection,
    form.setHexText,
    form.setPreviewImage,
    form.setSurveyQuestions,
    form.setCurrentStep,
    drafts.setError,
    isPremium ? currentNamespaceId : null  // Keep conditional logic here for template hook
  );
  
  // Wrap the handleImageChange to pass setError
  const handleImageChange = (e, setError) => {
    console.log('CreateCampaign: handleImageChange called', e);
    // If setError is provided, use it; otherwise, use drafts.setError
    const errorHandler = typeof setError === 'function' ? setError : drafts.setError;
    return form.handleImageChange(e, errorHandler);
  };

  // Expose setPreviewImage globally for direct access from components
  React.useEffect(() => {
    // Skip for non-premium users
    if (!isPremium) return;
    
    // Expose the setPreviewImage function globally
    window.setGlobalPreviewImage = (imageData) => {
      console.log('Global setPreviewImage called');
      form.setPreviewImage(imageData);
    };
    
    // Clean up when component unmounts
    return () => {
      delete window.setGlobalPreviewImage;
      // Clear form cache when navigating away
      form.clearFormCache();
    };
  }, [isPremium]);

  // Add event listener for beforeunload to clear cache when user leaves the page
  React.useEffect(() => {
    // Skip for non-premium users
    if (!isPremium) return;
    
    const handleBeforeUnload = () => {
      form.clearFormCache();
    };
    
    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Clean up
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isPremium]);

  // Delay showing errors to prevent flashing - no need to change this since it depends on draft.error
  useEffect(() => {
    if (drafts.error) {
      const timer = setTimeout(() => {
        setShowError(true);
      }, 500); // Delay showing error by 500ms
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [drafts.error]);

  // Set initialLoadComplete when namespaces and drafts are loaded
  useEffect(() => {
    if (!namespacesLoading && !drafts.isLoadingDrafts) {
      setInitialLoadComplete(true);
    }
  }, [namespacesLoading, drafts.isLoadingDrafts]);

  // Set isInitialRenderComplete after a minimum loading time
  useEffect(() => {
    const minLoadTime = 300;
    
    if (initialLoadComplete) {
      const frameId = requestAnimationFrame(() => {
        const timer = setTimeout(() => {
          setIsInitialRenderComplete(true);
        }, minLoadTime);
        
        return () => {
          clearTimeout(timer);
          cancelAnimationFrame(frameId);
        };
      });
      
      return () => cancelAnimationFrame(frameId);
    }
  }, [initialLoadComplete]);

  // Check for namespace when component mounts or namespace changes
  useEffect(() => {
    // Only proceed if we're not still loading namespaces
    if (!namespacesLoading) {
      if (!currentNamespaceId && contextNamespaces.length > 0) {
        // Only show error if namespaces are loaded but none is selected
        drafts.setError({
          type: 'error',
          message: 'No namespace selected. Please select a namespace to create campaigns.'
        });
      } else if (contextNamespaces.length === 0) {
        // If no namespaces exist at all
        drafts.setError({
          type: 'error',
          message: 'No namespaces available. Please create a namespace first.'
        });
      } else if (currentNamespaceId) {
        // Clear any namespace-related errors
        if (drafts.error && (
          drafts.error.message === 'No namespace selected. Please select a namespace to create campaigns.' ||
          drafts.error.message === 'No namespaces available. Please create a namespace first.'
        )) {
          drafts.setError(null);
        }
      }
    }
  }, [currentNamespaceId, namespacesLoading, contextNamespaces, drafts]);

  // Check if user has read-only permission for the current namespace
  const hasReadOnlyPermission = userPermission === 'readonly' || userPermission === 'read-only';
  
  // Debug permissions to console
  console.log('CreateCampaign permission check:', {
    currentNamespace,
    userPermission,
    hasReadOnlyPermission,
    currentNamespaceId,
    namespacesLoading,
    isPremium
  });

  // If user has read-only permission, show the permission error
  if (hasReadOnlyPermission && !namespacesLoading && currentNamespaceId) {
    return (
      <div className="p-6 w-full max-w-[1600px] mx-auto">
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create New Campaign</h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Set up your campaign details and customize your survey questions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6 items-start">
            <ReadOnlyPermissionMessage 
              userPermission={userPermission}
              currentNamespace={currentNamespace}
            />
            
            {/* Show phone preview with permission error message */}
            {form.showPreview && (
              <PhonePreview
                selectedTheme={form.selectedTheme}
                themes={themes}
                previewImage={form.previewImage}
                formData={{
                  name: "Permission Required",
                  title: "You don't have edit permissions",
                  description: "You need Editor or Admin permissions to create new campaigns."
                }}
                surveyQuestions={[]}
                currentStep={0}
                gradientColors={form.gradientColors}
                gradientDirection={form.gradientDirection}
                hexText={form.hexText}
                hasExplainerVideo={false}
                explainerVideo={null}
              />
            )}
          </div>
        </div>
        
        <div className="mt-16 text-center text-sm text-gray-500 dark:text-gray-400">
          © {new Date().getFullYear()} Advocacy Lab. All rights reserved.
        </div>
        
        {/* Include LoadingModal here to ensure it's shown even when the user has read-only permissions */}
        <LoadingModal
          isOpen={submit.loadingModal.isOpen}
          status={submit.loadingModal.status}
          error={submit.loadingModal.error}
          campaignId={submit.loadingModal.campaignId}
          campaignName={submit.loadingModal.campaignName}
          message={submit.loadingModal.message}
          videoUploadError={submit.loadingModal.videoUploadError}
          videoUploadWarning={submit.loadingModal.videoUploadWarning}
          resetForm={form.resetForm}
          clearFormCache={form.clearFormCache}
          onClose={() => {
            console.log('=== CreateCampaign: LoadingModal onClose called ===');
            console.log('Modal status when closing:', submit.loadingModal.status);
            console.log('Modal isOpen before closing:', submit.loadingModal.isOpen);
            // Just close the modal - don't reset the form here
            submit.setLoadingModal({ ...submit.loadingModal, isOpen: false, shouldStayOpen: false });
            console.log('Modal state updated to closed');
          }}
        />
      </div>
    );
  }
  
  // If user has reached campaign limit, show the limit reached component
  if (isPremium && hasReachedCampaignLimit && !isLoadingCampaignCount) {
    return (
      <div className="p-6 w-full max-w-[1600px] mx-auto">
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create New Campaign</h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Set up your campaign details and customize your survey questions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6 items-start">
            <CampaignLimitReached 
              userPlan={user?.planLevel || 'basic'} 
              campaignsCount={campaignsCount}
              planLimit={campaignLimit}
            />
            
            {/* Show phone preview with limit reached message */}
            {form.showPreview && (
              <PhonePreview
                selectedTheme={form.selectedTheme}
                themes={themes}
                previewImage={form.previewImage}
                formData={{
                  name: "Campaign Limit Reached",
                  title: "You've reached your plan limit",
                  description: "Please upgrade your plan or delete existing campaigns to create new ones."
                }}
                surveyQuestions={[]}
                currentStep={0}
                gradientColors={form.gradientColors}
                gradientDirection={form.gradientDirection}
                hexText={form.hexText}
                hasExplainerVideo={false}
                explainerVideo={null}
              />
            )}
          </div>
        </div>
        
        <div className="mt-16 text-center text-sm text-gray-500 dark:text-gray-400">
          © {new Date().getFullYear()} Advocacy Lab. All rights reserved.
        </div>
        
        {/* Include LoadingModal here to ensure it's shown even when the user has reached their limit */}
        <LoadingModal
          isOpen={submit.loadingModal.isOpen}
          status={submit.loadingModal.status}
          error={submit.loadingModal.error}
          campaignId={submit.loadingModal.campaignId}
          campaignName={submit.loadingModal.campaignName}
          message={submit.loadingModal.message}
          videoUploadError={submit.loadingModal.videoUploadError}
          videoUploadWarning={submit.loadingModal.videoUploadWarning}
          resetForm={form.resetForm}
          clearFormCache={form.clearFormCache}
          onClose={() => {
            console.log('=== CreateCampaign: LoadingModal onClose called ===');
            console.log('Modal status when closing:', submit.loadingModal.status);
            console.log('Modal isOpen before closing:', submit.loadingModal.isOpen);
            // Just close the modal - don't reset the form here
            submit.setLoadingModal({ ...submit.loadingModal, isOpen: false, shouldStayOpen: false });
            console.log('Modal state updated to closed');
          }}
        />
      </div>
    );
  }
  
  // Show skeleton loader when loading campaign limit data
  if (isPremium && (!isInitialRenderComplete || isLoadingCampaignCount || namespacesLoading)) {
    return (
      <div className="p-6 w-full max-w-[1600px] mx-auto" style={styles.container}>
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create New Campaign</h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Set up your campaign details and customize your survey questions.
            </p>
            {/* Progress bar - showing 1/5 steps = 20% */}
            <div className="relative max-w-[800px] h-1 bg-gray-200 dark:bg-gray-800 mt-4">
              <div
                className="absolute inset-y-0 left-0 bg-primary-600 dark:bg-primary-400 transition-all duration-500"
                style={{ width: '16.7%' }}
              />
            </div>
          </div>
          
          <CreateCampaignSkeleton />
        </div>
        
        <div className="mt-16 text-center text-sm text-gray-500 dark:text-gray-400">
          © {new Date().getFullYear()} Advocacy Lab. All rights reserved.
        </div>
        
        {/* Include LoadingModal here to ensure it's shown even when loading */}
        <LoadingModal
          isOpen={submit.loadingModal.isOpen}
          status={submit.loadingModal.status}
          error={submit.loadingModal.error}
          campaignId={submit.loadingModal.campaignId}
          campaignName={submit.loadingModal.campaignName}
          message={submit.loadingModal.message}
          videoUploadError={submit.loadingModal.videoUploadError}
          videoUploadWarning={submit.loadingModal.videoUploadWarning}
          resetForm={form.resetForm}
          clearFormCache={form.clearFormCache}
          onClose={() => {
            console.log('=== CreateCampaign: LoadingModal onClose called ===');
            console.log('Modal status when closing:', submit.loadingModal.status);
            console.log('Modal isOpen before closing:', submit.loadingModal.isOpen);
            // Just close the modal - don't reset the form here
            submit.setLoadingModal({ ...submit.loadingModal, isOpen: false, shouldStayOpen: false });
            console.log('Modal state updated to closed');
          }}
        />
      </div>
    );
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto" style={styles.container}>
      <SEOHead pageType="app-create-campaign" />
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create New Campaign</h1>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Set up your campaign details and customize your survey questions.
          </p>
          <div className="relative max-w-[800px] h-1 bg-gray-200 dark:bg-gray-800 mt-4">
            <div
              className="absolute inset-y-0 left-0 bg-primary-600 dark:bg-primary-400 transition-all duration-500"
              style={{ width: `${((form.currentStep + 1) / 6) * 100}%` }}
            />
          </div>
        </div>

        {/* Namespace warning - only show after initial load and with a delay */}
        {!currentNamespaceId && !namespacesLoading && contextNamespaces.length > 0 && initialLoadComplete && showError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-900/50 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400 dark:text-amber-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">Namespace Required</h3>
                <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                  <p>Please select a namespace to create and manage campaigns. Campaigns are shared within a namespace.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Only show drafts section for premium users */}
        {isPremium && (
          <DraftSection
            drafts={drafts.drafts}
            isLoadingDrafts={namespacesLoading || drafts.isLoadingDrafts}
            isDraftsOpen={isDraftsOpen}
            setIsDraftsOpen={setIsDraftsOpen}
            handleLoadDraft={drafts.handleLoadDraft}
            handleDeleteDraft={drafts.handleDeleteDraft}
            selectedDraftId={drafts.selectedDraftId}
            setSelectedDraftId={drafts.setSelectedDraftId}
            setIsEditingDraft={drafts.setIsEditingDraft}
            isDeletingDraft={drafts.isDeletingDraft}
            deleteMessage={drafts.deleteMessage}
          />
        )}

        {drafts.error && drafts.error.type === 'error' && showError && (
          <div className="mb-4 max-w-[800px] flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {drafts.error.message}
          </div>
        )}

        <div className={`grid grid-cols-1 ${form.showPreview ? 'lg:grid-cols-[800px,auto]' : ''} gap-6 items-start ${!form.showPreview ? 'w-full' : ''}`}>
          <CampaignFormContainer
            currentStep={form.currentStep}
            formData={form.formData}
            handleInputChange={form.handleInputChange}
            updateFormData={form.updateFormData}
            aiGeneratedFields={form.aiGeneratedFields}
            setAiGeneratedFields={form.setAiGeneratedFields}
            surveyQuestions={form.surveyQuestions}
            setSurveyQuestions={form.setSurveyQuestions}
            handleNext={form.handleNext}
            handlePrevious={form.handlePrevious}
            handleSubmit={submit.handleSubmit}
            isSubmitting={submit.isSubmitting}
            isSavingDraft={drafts.isSavingDraft}
            handleSaveDraft={drafts.handleSaveDraft}
            isEditingDraft={drafts.isEditingDraft}
            setIsAIModalOpen={setIsAIModalOpen}
            handleUseTemplate={template.handleUseTemplate}
            clearSelectedTemplate={template.clearSelectedTemplate}
            selectedTemplate={template.selectedTemplate}
            selectedTheme={form.selectedTheme}
            setSelectedTheme={form.setSelectedTheme}
            gradientColors={form.gradientColors}
            setGradientColors={form.setGradientColors}
            gradientDirection={form.gradientDirection}
            setGradientDirection={form.setGradientDirection}
            hexText={form.hexText}
            setHexText={form.setHexText}
            themes={themes}
            previewImage={form.previewImage}
            handleImageChange={handleImageChange}
            handleRemoveImage={form.handleRemoveImage}
            setIsHelpOpen={setIsHelpOpen}
            hasExplainerVideo={form.hasExplainerVideo}
            setHasExplainerVideo={form.setHasExplainerVideo}
            explainerVideo={form.explainerVideo}
            setExplainerVideo={form.setExplainerVideo}
            error={drafts.error}
            currentNamespace={currentNamespace}
            userPermission={userPermission}
            renderCustomButtons={form.currentStep === 0}
            renderCustomButtonsContent={
              form.currentStep === 0 ? (
                <IntroductionPageButtons 
                  setIsVideoModalOpen={setIsVideoModalOpen} 
                  onNext={form.handleNext}
                />
              ) : null
            }
          />

          <PhonePreview
            selectedTheme={form.selectedTheme}
            themes={themes}
            previewImage={form.previewImage}
            formData={form.formData}
            surveyQuestions={form.surveyQuestions}
            currentStep={form.currentStep}
            gradientColors={form.gradientColors}
            gradientDirection={form.gradientDirection}
            hexText={form.hexText}
            hasExplainerVideo={form.hasExplainerVideo}
            explainerVideo={form.explainerVideo}
          />
        </div>
      </div>

      <Footer />
      
      <LoadingModal
        isOpen={submit.loadingModal.isOpen}
        status={submit.loadingModal.status}
        error={submit.loadingModal.error}
        campaignId={submit.loadingModal.campaignId}
        campaignName={submit.loadingModal.campaignName}
        message={submit.loadingModal.message}
        videoUploadError={submit.loadingModal.videoUploadError}
        videoUploadWarning={submit.loadingModal.videoUploadWarning}
        resetForm={form.resetForm}
        clearFormCache={form.clearFormCache}
        onClose={() => {
          console.log('=== CreateCampaign: LoadingModal onClose called ===');
          console.log('Modal status when closing:', submit.loadingModal.status);
          console.log('Modal isOpen before closing:', submit.loadingModal.isOpen);
          // Just close the modal - don't reset the form here
          submit.setLoadingModal({ ...submit.loadingModal, isOpen: false, shouldStayOpen: false });
          console.log('Modal state updated to closed');
        }}
      />

      <AIModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        aiPrompt={ai.aiPrompt}
        setAiPrompt={ai.setAiPrompt}
        handleAIGenerate={ai.handleAIGenerate}
        isGenerating={ai.isGenerating}
        aiError={ai.aiError}
      />

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
      />

      <CampaignTemplateModal
        isOpen={template.isTemplateModalOpen}
        onClose={() => template.setIsTemplateModalOpen(false)}
        onSelectTemplate={template.handleSelectTemplate}
      />
      
      <VideoModal 
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        title="Campaign Demo Video"
        videoUrl="https://www.youtube.com/embed/7dtzDKOJwE4?autoplay=1"
      />
    </div>
  );
}
